import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal, NgbDropdownModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { CompliancesLibraryService } from '../../../services/compliances-library.service';
import { ComplianceOverviewService } from '../../../../compliance-overview/services/compliance-overview.service';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { AccountFilterData } from '../../../../../../_shared/models';
import { ExcelReviewModalComponent } from '../../../../../_shared/components/modals/excel-review-modal/excel-review-modal.component';
import { groupComplianceImports, validateReviewItem } from '../../../helpers/compliance-upload.helper';
import { ComplianceFrequency, ComplianceImport, ComplianceStandard, ReviewComplianceItem, RoutineType } from '../../../models/compliances.model';
import { ComplianceRegion } from '../../../../compliance-overview/models/compliance-item.model';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { LookupLabelPipe } from '../../../../../../_shared/pipes/lookup-label.pipe';
import { translateComplianceFrequency, translateRoutineTypeName } from '../../../../_shared/helpers/compliance-lookup-label.helper';

@Component({
  selector: 'app-compliance-review-excel-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgbDropdownModule,
    NgbTooltipModule,
    ExcelReviewModalComponent,
    TranslocoPipe,
    LookupLabelPipe
  ],
  templateUrl: './compliance-review-excel-modal.component.html',
  styleUrl: './compliance-review-excel-modal.component.scss'
})
export class ComplianceReviewExcelModalComponent implements OnInit, OnDestroy {
  @Input() imports: ComplianceImport[] = [];
  @Input() accountIds: number[] = [];

  private activeModal = inject(NgbActiveModal);
  private libraryService = inject(CompliancesLibraryService);
  private overviewService = inject(ComplianceOverviewService);
  private errorService = inject(ErrorHandlerService);
  private toastr = inject(ToastrService);
  private transloco = inject(TranslocoService);
  private cancellation = new Subject<void>();
  private standardsCancellation = new Subject<void>();

  items: ReviewComplianceItem[] = [];
  selected: ReviewComplianceItem | null = null;
  categoryIndexes: number[] = [];

  regions: ComplianceRegion[] = [];
  frequencies: ComplianceFrequency[] = [];
  routineTypes: RoutineType[] = [];
  existingStandards: ComplianceStandard[] = [];
  isLoading = true;
  isLoadingStandards = false;
  isImporting = false;
  serverErrors: string[] = [];

  accountFilter: AccountFilterData = { accountIds: [] };
  selectedAccountIds: number[] = [];

  matchesSearch = (item: ReviewComplianceItem, term: string): boolean =>
    item.name.toLowerCase().includes(term)
    || item.regionNames.some(regionName => regionName.toLowerCase().includes(term));

  itemName = (item: ReviewComplianceItem): string => item.name;

  ngOnInit(): void {
    this.accountFilter = { accountIds: this.accountIds };
    this.selectedAccountIds = [...this.accountIds];
    this.items = groupComplianceImports(this.imports);
    this.categoryIndexes = Array.from(
      { length: Math.max(1, ...this.items.map(item => item.categories.length)) },
      (_, index) => index);
    this.loadLookupsAsync();
  }

  ngOnDestroy(): void {
    this.cancellation.next();
    this.cancellation.complete();
    this.standardsCancellation.next();
    this.standardsCancellation.complete();
  }

  private async loadLookupsAsync(): Promise<void> {
    try {
      // Existing standards load in loadExistingStandardsAsync once accounts are selected.
      const [regionsResponse, frequenciesResponse, routineTypesResponse] = await Promise.all([
        this.overviewService.getComplianceRegionsAsync(this.cancellation),
        this.libraryService.getComplianceFrequenciesAsync(this.cancellation),
        this.libraryService.getRoutineTypesAsync(this.cancellation)
      ]);
      this.regions = regionsResponse.data ?? [];
      this.frequencies = frequenciesResponse.data ?? [];
      this.routineTypes = routineTypesResponse.data ?? [];

      this.items.forEach(item => this.validateItem(item));
      this.selected = this.items.find(item => item.errors.length > 0) ?? this.items[0] ?? null;
      this.isLoading = false;
    } catch (error: any) {
      this.errorService.handleError(error, {
        callback: () => {
          this.isLoading = false;
        }
      });
    }
  }

  get errorCount(): number {
    return this.items.filter(item => item.errors.length > 0).length;
  }

  get canImport(): boolean {
    return !this.isImporting
      && !this.isLoadingStandards
      && this.items.length > 0
      && this.errorCount === 0
      && this.selectedAccountIds.length > 0;
  }

  onItemRemoved(): void {
    this.items.forEach(item => this.validateItem(item));
  }

  onAccountFilterChange(filter: AccountFilterData): void {
    this.selectedAccountIds = filter.accountIds;
    this.loadExistingStandardsAsync();
  }

  private async loadExistingStandardsAsync(): Promise<void> {
    this.standardsCancellation.next();
    if (this.selectedAccountIds.length === 0) {
      this.isLoadingStandards = false;
      if (this.existingStandards.length > 0) {
        this.existingStandards = [];
        this.items.forEach(item => this.validateItem(item));
      }
      return;
    }

    this.isLoadingStandards = true;
    try {
      const response = await this.libraryService.getComplianceStandardsAsync(this.selectedAccountIds, this.standardsCancellation);
      this.existingStandards = response.data ?? [];
      this.items.forEach(item => this.validateItem(item));
      this.isLoadingStandards = false;
    } catch (error: any) {
      this.errorService.handleError(error, {
        callback: () => {
          this.isLoadingStandards = false;
        }
      });
    }
  }

  isPeriodic(item: ReviewComplianceItem): boolean {
    return item.complianceType.trim().toLowerCase() === 'periodic';
  }

  isRoutine(item: ReviewComplianceItem): boolean {
    return item.complianceType.trim().toLowerCase() === 'routine';
  }

  hasValidType(item: ReviewComplianceItem): boolean {
    return this.isPeriodic(item) || this.isRoutine(item);
  }

  displayType(item: ReviewComplianceItem): string {
    return item.complianceType.trim() || '-';
  }

  displayFrequency(item: ReviewComplianceItem): string {
    if (this.isPeriodic(item)) return this.displayScheduleName(item) || '-';
    return this.isRoutine(item) ? this.transloco.translate('compliance.library.reviewExcelModal.notApplicable') : '-';
  }

  displayRoutineType(item: ReviewComplianceItem): string {
    if (this.isRoutine(item)) return this.displayScheduleName(item) || '-';
    return this.isPeriodic(item) ? this.transloco.translate('compliance.library.reviewExcelModal.notApplicable') : '-';
  }

  /** Translated schedule name for display; `scheduleName` itself stays the raw name the import sends. */
  displayScheduleName(item: ReviewComplianceItem): string {
    const name = item.scheduleName.trim();
    if (!name) return '';
    return this.isPeriodic(item)
      ? translateComplianceFrequency(this.transloco, name)
      : translateRoutineTypeName(this.transloco, name);
  }

  unknownRegionNames(item: ReviewComplianceItem): string[] {
    return item.regionNames.filter(regionName =>
      !this.regions.some(region => region.name.toLowerCase() === regionName.toLowerCase()));
  }

  onSelectedNameChanged(name: string): void {
    if (!this.selected) return;
    this.selected.name = name.trim();
    this.items.forEach(item => this.validateItem(item));
  }

  selectComplianceType(type: 'Periodic' | 'Routine'): void {
    if (!this.selected || this.selected.complianceType === type) return;
    this.selected.complianceType = type;
    this.selected.scheduleName = '';
    this.validateItem(this.selected);
  }

  selectSchedule(name: string): void {
    if (!this.selected) return;
    this.selected.scheduleName = name;
    this.validateItem(this.selected);
  }

  isRegionSelected(item: ReviewComplianceItem, regionName: string): boolean {
    return item.regionNames.some(existing => existing.toLowerCase() === regionName.toLowerCase());
  }

  toggleRegion(regionName: string): void {
    if (!this.selected) return;
    const index = this.selected.regionNames.findIndex(existing => existing.toLowerCase() === regionName.toLowerCase());
    if (index >= 0) {
      this.selected.regionNames.splice(index, 1);
    } else {
      this.selected.regionNames.push(regionName);
    }
    this.validateItem(this.selected);
  }

  private validateItem(item: ReviewComplianceItem): void {
    item.errors = validateReviewItem(item, this.regions, this.frequencies, this.routineTypes, this.existingStandards);

    const name = item.name.toLowerCase();
    if (name && this.items.some(other => other !== item && other.name.toLowerCase() === name)) {
      item.errors = [...item.errors, { message: this.transloco.translate('compliance.library.reviewExcelModal.duplicateNameError'), kind: 'incorrect' }];
    }
  }

  async importAsync(): Promise<void> {
    if (!this.canImport) return;
    this.isImporting = true;
    this.serverErrors = [];
    try {
      await this.libraryService.bulkUploadCompliancesAsync({
        accountIds: this.selectedAccountIds,
        compliances: this.toComplianceImports()
      }, this.cancellation);
      this.toastr.success(this.transloco.translate('compliance.library.reviewExcelModal.toasts.imported'));
      this.activeModal.close('imported');
    } catch (error: any) {
      if (error?.error?.errorCode === 'compliance/invalid-compliance-upload') {
        this.serverErrors = error.error.data?.length > 0
          ? error.error.data
          : [error.error.message || this.transloco.translate('compliance.library.reviewExcelModal.importRejected')];
        this.isImporting = false;
        return;
      }

      this.errorService.handleError(error, {
        callback: () => {
          this.isImporting = false;
        }
      });
    }
  }

  private toComplianceImports(): ComplianceImport[] {
    const createdBy = this.imports[0]?.createdBy ?? '';
    const createdAt = this.imports[0]?.createdAt ?? new Date();
    return this.items.flatMap(item => item.categories.map(category => ({
      complianceStandardName: item.name,
      regions: item.regionNames.join(', '),
      complianceType: item.complianceType,
      frequency: item.scheduleName,
      complianceCategory: category.name,
      checklistType: category.checklistType,
      requirements: category.requirements.join(', '),
      createdBy,
      createdAt
    })));
  }

  close(): void {
    this.activeModal.dismiss();
  }
}
